import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Info, ArrowLeft } from "lucide-react";

const fadeUp = (delay: number) => ({
  initial: { opacity: 0, y: 12 },
  animate: { opacity: 1, y: 0 },
  transition: { delay, duration: 0.35 },
});

const reviewCards = [
  {
    title: "Email Verification",
    badge: "WELCH.PATR@GMAIL.COM",
    desc: "Email verified, but account history is relatively short (2 years).",
    color: "orange",
  },
  {
    title: "Name Match",
    badge: "MISMATCH DETECTED",
    desc: "Name provided ('Patrick Welch') does not consistently match metadata from connected accounts ('Pat Welch', 'P. Welch').",
    color: "red",
  },
  {
    title: "Connection Attempts",
    badge: "MULTIPLE FAILURES",
    desc: "Tried to log into multiple different connections; none were able to successfully authenticate or match metadata.",
    color: "red",
  },
  {
    title: "Professional Vouch",
    badge: "NOT CONFIRMED",
    desc: "The requested vouch did not confirm the applicant's identity or relationship context.",
    color: "orange",
  },
];

const iconColors: Record<string, string> = {
  orange: "bg-orange-100 text-orange-600",
  red: "bg-red-100 text-red-600",
};

export default function ManualReviewResult() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-muted/30 flex items-center justify-center p-4">
      <motion.div {...fadeUp(0)} className="w-full max-w-3xl">
        <Card className="overflow-hidden shadow-xl border-0">
          {/* Orange header */}
          <div className="bg-gradient-to-br from-orange-400 to-orange-500 text-white p-8 pb-10 relative">
            <p className="text-xs font-semibold tracking-widest uppercase opacity-80 mb-1">
              Verification Result
            </p>
            <h1 className="text-4xl font-bold tracking-tight">Manual Review</h1>

            <div className="absolute top-6 right-6 bg-white/20 backdrop-blur-sm rounded-lg px-5 py-3 text-right">
              <p className="text-[10px] font-semibold tracking-widest uppercase opacity-80">
                Applicant
              </p>
              <p className="text-lg font-bold leading-tight">Patrick Welch</p>
              <p className="text-xs opacity-80">Turnaround: ~2 mins</p>
            </div>
          </div>

          <CardContent className="p-8 space-y-8">
            {/* Summary + Routing Context */}
            <div className="grid md:grid-cols-2 gap-6">
              <motion.div {...fadeUp(0.1)}>
                <p className="text-xs font-semibold tracking-widest uppercase text-orange-500 mb-2">
                  Summary
                </p>
                <p className="text-muted-foreground leading-relaxed">
                  Patrick Welch requires manual intervention due to significant inconsistencies and failed verification signals.
                </p>
              </motion.div>
              <motion.div {...fadeUp(0.15)}>
                <Card className="border bg-muted/40">
                  <CardContent className="p-4">
                    <p className="text-xs font-semibold tracking-widest uppercase text-muted-foreground mb-1">
                      Routing Context
                    </p>
                    <p className="font-semibold italic text-sm">
                      "Multiple mismatches in name, failed connection attempts, and vouch not confirmed"
                    </p>
                    <p className="text-[10px] tracking-widest uppercase text-muted-foreground mt-1">
                      Verification was tailored to address this specific exception case.
                    </p>
                  </CardContent>
                </Card>
              </motion.div>
            </div>

            {/* Review cards */}
            <div className="grid md:grid-cols-2 gap-4">
              {reviewCards.map((card, i) => (
                <motion.div key={card.title} {...fadeUp(0.2 + i * 0.05)}>
                  <Card className="border bg-background hover:shadow-md transition-shadow">
                    <CardContent className="p-4 flex gap-3">
                      <div className="mt-0.5">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${iconColors[card.color]}`}>
                          <Info className="w-4 h-4" />
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <span className="font-semibold text-sm">{card.title}</span>
                          <Badge variant="secondary" className="text-[10px] tracking-wide font-medium">
                            {card.badge}
                          </Badge>
                        </div>
                        <p className="text-xs text-muted-foreground leading-relaxed">
                          {card.desc}
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>

            {/* Action buttons */}
            <motion.div {...fadeUp(0.45)} className="grid md:grid-cols-2 gap-4 pt-2">
              <Button
                size="lg"
                className="bg-foreground hover:bg-foreground/90 text-background font-semibold"
                onClick={() => navigate("/dashboard")}
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Dashboard
              </Button>
            </motion.div>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
}
